import { useEffect, useState, useMemo } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import LayeredStack from "@/components/LayeredStack";
import { Spinner, PageLoader } from "@/components/Kit";
import { playSfx, playJingle } from "@/lib/sound";
import { ChevronLeft, ChevronRight, Check } from "lucide-react";

const STEPS = ["legs", "torso", "arms", "head", "cpu", "core", "finalize"];
const LABELS = { legs: "Legs", torso: "Torso", arms: "Arms", head: "Head", cpu: "CPU", core: "Core", finalize: "Finalize" };
const HINTS = {
  legs: "Determines stance & idle motion",
  torso: "The body core of your unit",
  arms: "Manipulators & battle limbs",
  head: "Optics and expression",
  cpu: "Sets temperament & dialogue",
  core: "Sets your neon glow color",
};

export default function AssemblyMatrix() {
  const navigate = useNavigate();
  const { refreshPet, refreshUser } = useOutletContext();
  const [cat, setCat] = useState(null);
  const [step, setStep] = useState(0);
  const [sel, setSel] = useState({ legs: null, torso: null, arms: null, head: null, cpu: null, core: null });
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/catalog").then((r) => {
      setCat(r.data);
      setSel({
        legs: r.data.legs[0].id, torso: r.data.torso[0].id, arms: r.data.arms[0].id,
        head: r.data.heads[0].id, cpu: r.data.cpus[0].id, core: r.data.cores[0].id,
      });
    });
  }, []);

  // build lookup maps + live layer art from current selection
  const maps = useMemo(() => {
    if (!cat) return null;
    return {
      legs: Object.fromEntries(cat.legs.map((x) => [x.id, x])),
      torso: Object.fromEntries(cat.torso.map((x) => [x.id, x])),
      arms: Object.fromEntries(cat.arms.map((x) => [x.id, x])),
      head: Object.fromEntries(cat.heads.map((x) => [x.id, x])),
      cpu: Object.fromEntries(cat.cpus.map((x) => [x.id, x])),
      core: Object.fromEntries(cat.cores.map((x) => [x.id, x])),
    };
  }, [cat]);

  if (!cat || !maps) return <PageLoader label="Warming up the Assembly Bay" />;

  const stepKey = STEPS[step];
  const coreObj = maps.core[sel.core] || {};
  const cpuObj = maps.cpu[sel.cpu] || {};
  const legObj = maps.legs[sel.legs] || {};

  const liveLayers = {
    legs: maps.legs[sel.legs]?.art,
    arms: maps.arms[sel.arms]?.art,
    torso: maps.torso[sel.torso]?.art,
    head: maps.head[sel.head]?.art,
  };

  const pick = (key, id) => { setSel((s) => ({ ...s, [key]: id })); playSfx("pop"); };

  const finalize = async () => {
    if (!name.trim()) { toast.error("Designate a unit name"); return; }
    setBusy(true);
    try {
      await api.post("/pet/assemble", { name: name.trim(), ...sel });
      playJingle(sel.cpu);
      await refreshPet();
      if (refreshUser) await refreshUser();
      toast.success(`PET_CREATION_SUCCESSFUL // WELCOME TO ORBIT, ${name.toUpperCase()}`);
      navigate("/app/station");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const gridFor = () => {
    if (stepKey === "legs") return [cat.legs, "legs"];
    if (stepKey === "torso") return [cat.torso, "torso"];
    if (stepKey === "arms") return [cat.arms, "arms"];
    if (stepKey === "head") return [cat.head ? cat.head : cat.heads, "head"];
    if (stepKey === "cpu") return [cat.cpus, "cpu"];
    if (stepKey === "core") return [cat.cores, "core"];
    return [null, null];
  };
  const [items, key] = gridFor();
  const isVisual = ["legs", "torso", "arms", "head"].includes(stepKey);

  return (
    <div data-testid="assembly-matrix">
      {/* step bar */}
      <div className="panel px-2 py-2 mb-5 flex items-center gap-1 overflow-x-auto no-scrollbar hud-corner">
        <span className="label-cap px-2 hidden sm:block">ASSEMBLE</span>
        {STEPS.map((s, i) => (
          <button key={s} data-testid={`step-${s}`} onClick={() => setStep(i)}
            className={`px-3 py-1.5 rounded-[var(--radius)] text-xs font-bold uppercase tracking-wider mono whitespace-nowrap transition-colors ${i === step ? "bg-primary text-primary-foreground" : i < step ? "text-primary" : "text-muted-foreground"}`}>
            {i < step && "✓ "}{LABELS[s]}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-[1fr_minmax(240px,340px)] gap-5">
        {/* selection area */}
        <div className="panel p-5 min-h-[420px]">
          {stepKey !== "finalize" ? (
            <>
              <h2 className="font-head font-bold text-xl mb-1">Select {LABELS[stepKey]}</h2>
              <p className="label-cap mb-4">{HINTS[stepKey]}</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {items.map((it) => {
                  const active = sel[key] === it.id;
                  if (key === "core") {
                    return (
                      <button key={it.id} data-testid={`opt-${it.id}`} onClick={() => pick(key, it.id)}
                        className={`aspect-square rounded-[var(--radius)] border p-1 flex flex-col items-center justify-center gap-1 transition-transform hover:-translate-y-0.5 ${active ? "border-primary" : "border-border"}`}
                        style={active ? { boxShadow: `0 0 16px -4px ${it.color}` } : {}}>
                        <span className="w-8 h-8 rounded-full" style={{ background: it.color, boxShadow: `0 0 14px ${it.color}` }} />
                        <span className="text-[10px] font-bold text-center leading-none mono">{it.name}</span>
                      </button>
                    );
                  }
                  if (key === "cpu") {
                    return (
                      <button key={it.id} data-testid={`opt-${it.id}`} onClick={() => pick(key, it.id)}
                        className={`col-span-3 sm:col-span-2 rounded-[var(--radius)] border p-3 text-left transition-transform hover:-translate-y-0.5 ${active ? "border-primary panel-glow" : "border-border"}`}>
                        <div className="font-head font-bold text-sm">{it.name}</div>
                        <div className="label-cap text-[9px] mt-0.5">{it.temperament}</div>
                        <p className="text-xs text-muted-foreground italic mt-1 leading-snug">"{it.sample}"</p>
                      </button>
                    );
                  }
                  // visual parts: show the part art thumbnail
                  return (
                    <button key={it.id} data-testid={`opt-${it.id}`} onClick={() => pick(key, it.id)}
                      className={`rounded-[var(--radius)] border overflow-hidden flex flex-col items-center transition-transform hover:-translate-y-0.5 ${active ? "border-primary panel-glow" : "border-border"}`}>
                      <div className="w-full aspect-[0.67] bg-background/60 grid place-items-center overflow-hidden">
                        <img src={it.art} alt={it.name} draggable={false} className="w-full h-full object-contain" />
                      </div>
                      <span className="text-[10px] font-bold text-center leading-none mono py-1.5 px-1 truncate w-full">{it.name}</span>
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <>
              <h2 className="font-head font-bold text-xl mb-4">Finalize Unit</h2>
              <label className="label-cap block mb-2">Designation</label>
              <input data-testid="pet-name-input" autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={24}
                placeholder="e.g. AXIOM-7" className="input-field mb-4" onKeyDown={(e) => e.key === "Enter" && finalize()} />
              <div className="space-y-1 text-sm mono text-muted-foreground">
                <div>LEGS: <span className="text-foreground">{legObj.name}</span> ({legObj.rig})</div>
                <div>TORSO: <span className="text-foreground">{(maps.torso[sel.torso] || {}).name}</span></div>
                <div>ARMS: <span className="text-foreground">{(maps.arms[sel.arms] || {}).name}</span></div>
                <div>HEAD: <span className="text-foreground">{(maps.head[sel.head] || {}).name}</span></div>
                <div>CPU: <span className="text-foreground">{cpuObj.name}</span> — {cpuObj.temperament}</div>
                <div>CORE: <span style={{ color: coreObj.color }}>{coreObj.name}</span></div>
              </div>
            </>
          )}
        </div>

        {/* live preview */}
        <div className="panel-glow p-5 flex flex-col items-center justify-center hud-corner">
          <div className="label-cap mb-2 self-start">Real-time Preview</div>
          <LayeredStack layers={liveLayers} core={coreObj.color} rig={legObj.rig} cpu={sel.cpu} size={280} />
          <div className="mt-4 text-center">
            <div className="font-head font-bold">{name || "UNNAMED UNIT"}</div>
            <div className="label-cap mt-1">{cpuObj.temperament}</div>
            {isVisual && <p className="text-xs text-muted-foreground italic mt-2 max-w-[240px]">{(items.find((x) => x.id === sel[key]) || {}).desc}</p>}
          </div>
        </div>
      </div>

      {/* nav */}
      <div className="flex justify-between mt-5">
        <button data-testid="prev-btn" disabled={step === 0} onClick={() => setStep((s) => s - 1)} className="btn-ghost"><ChevronLeft className="w-4 h-4" /> Prev</button>
        {stepKey !== "finalize" ? (
          <button data-testid="next-btn" onClick={() => setStep((s) => s + 1)} className="btn-primary">Next <ChevronRight className="w-4 h-4" /></button>
        ) : (
          <button data-testid="assemble-btn" onClick={finalize} disabled={busy} className="btn-primary">
            {busy ? <Spinner className="w-4 h-4" /> : <><Check className="w-4 h-4" /> Assemble</>}
          </button>
        )}
      </div>
    </div>
  );
}
